import React, {
  ComponentType,
  FC,
  lazy,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { Switch, useRouteMatch, useLocation } from 'react-router-dom';
import Route, { AnonymousRoute } from 'components/common/Route';
import { CartProvider } from './CartProvider';
import Home from './Home';
import InvoicePDF from './InvoicePDF';
import ManageSubscription from './ManageSubscription';
import RefreshMonitor from './RefreshMonitor';
import PageError from 'components/common/PageError';
import Invoice from './Invoice';
import styled from 'styled-components';
import SecondaryNavigation from 'components/common/SecondaryNavigation';
import { subNavLinks } from 'config/secondaryNavigation';
import { Flex } from '@resideo/blueprint-react';
import { CartContext } from './CartProvider';
import { graphql } from 'babel-plugin-relay/macro';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import { ServicesAndSubscriptionsRoutesQuery } from './__generated__/ServicesAndSubscriptionsRoutesQuery.graphql';

type LazyModulePromise<T = Record<string, unknown>> = Promise<{
  default: ComponentType<T>;
}>;

const SelectPlan = lazy((): LazyModulePromise => import('./SelectPlan'));
const Cart = lazy((): LazyModulePromise => import('./Cart'));
const PDP = lazy((): LazyModulePromise => import('./PDP'));

const MainContainer = styled(Flex)`
  border-bottom: 1px solid lightgray;
  height: 100%;
`;

const ContentWrapper = styled(Flex)<{
  withSubNav?: boolean;
}>`
  height: 100%;
  padding-top: ${({ withSubNav }) => (withSubNav ? '4rem' : '0')};

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    padding-top: 0;
  }
`;

const MyBusinessLayout: FC<any> = ({ children, menuItems, pathName }) => {
  const environment = useRelayEnvironment();
  const { cartActions } = useContext(CartContext);
  const path: string = pathName ?? '';
  const withSubNav = !(
    path.includes('cart') ||
    path.includes('pdp') ||
    path.includes('select-plan') ||
    path.includes('invoice')
  );
  const programProductData = useCallback(() => {
    fetchQuery<ServicesAndSubscriptionsRoutesQuery>(
      environment,
      graphql`
        query ServicesAndSubscriptionsRoutesQuery {
          partnerProgramProducts {
            edges {
              cursor
              node {
                ... on PartnerProgramProduct {
                  id
                  name
                  programOffering
                  variation
                  billingCycle
                  orderPrice
                  recurringPrice
                  recurringPriceDescription
                  sku
                  skuType
                  partnerProgram {
                    id
                    name
                  }
                  parentPartnerProgramProduct {
                    id
                  }
                }
              }
            }
          }
        }
      `,
      {}
    ).subscribe({
      next: data => {
        const theProducts = data?.partnerProgramProducts?.edges ?? [];
        const flatProducts = theProducts?.map(obj => {
          if (!obj) return {};
          return {
            ...obj.node,
            ...{
              partnerProgram: obj.node.partnerProgram
                ? { ...obj.node.partnerProgram }
                : null,
            },
            ...{
              parentPartnerProgramProduct: obj.node.parentPartnerProgramProduct
                ? { ...obj.node.parentPartnerProgramProduct }
                : null,
            },
          };
        });
        cartActions.setProgramProducts(flatProducts);
      },
      error: () => {
        cartActions.setProgramProducts([]);
      },
    });
  }, [environment]);

  useEffect(() => {
    programProductData();
  }, []);

  return (
    <MainContainer flexDirection='column' width={1}>
      {withSubNav && <SecondaryNavigation menuItems={menuItems} />}
      <ContentWrapper withSubNav={withSubNav}>{children}</ContentWrapper>
    </MainContainer>
  );
};

const ServicesAndSubscriptionRoutes: FC = () => {
  const { path } = useRouteMatch();
  const { pathname } = useLocation();

  return (
    <CartProvider>
      <MyBusinessLayout menuItems={subNavLinks()} pathName={pathname}>
        <Switch>
          <Route exact path={path} component={Home} />
          <Route
            exact
            path={`${path}manage-subscription/:id`}
            component={ManageSubscription}
          />
          <Route path={`${path}refresh-monitor`} component={RefreshMonitor} />
          <Route path={`${path}invoicepdf`} component={InvoicePDF} />
          <Route
            path={`${path}invoice/:id/:subid/:invoiceNumber`}
            component={Invoice}
          />
          <Route path={`${path}cart`} component={Cart} />
          <Route path={`${path}pdp`} component={PDP} />
          <Route path={`${path}select-plan`} component={SelectPlan} />
          <AnonymousRoute>
            <PageError />
          </AnonymousRoute>
        </Switch>
      </MyBusinessLayout>
    </CartProvider>
  );
};

export default ServicesAndSubscriptionRoutes;
